// Libs
import classNames from 'classnames/bind';
import {
  Heading,
  IconButton,
  IconCheckLine,
  IconEditLine,
  IconLinkLine,
  IconQuizLine,
  Spinner,
  Table,
  Text,
  TextInput,
  View,
} from '@instructure/ui';
import { useTranslation } from 'react-i18next';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
// Components, Layouts, Pages
import { BasePagination, BaseSelect, Loading } from '~/components/common';
// Others
import { useAppDispatch, useAppSelector } from '~/utils/redux/hooks';
import { DEFAULT_LIMIT, DEFAULT_LIMIT_PAGE, DEFAULT_PAGE } from '~/utils/constants/common';
import { userActions } from '~/thunks/user/userSlice';
import { IStudent } from '~/utils/interface/profile';
import { getStudents, updateUsers } from '~/thunks/user/userThunk';
import { IQueryGetStudent } from '~/utils/interface/account';
import { IBaseSelectOption } from '~/utils/interface/common';
import { getCourses } from '~/thunks/course/courseThunk';
import { StorageEnum } from '~/utils/enum/base';
// Styles, images, icons
import styles from './Material.module.scss';
import { icons } from '~/assets';
import { IModuleDetail, IQueryGetModule } from '~/utils/interface/module';
import { getModules } from '~/thunks/module/moduleThunk';
import { MaterialTypeEnum } from '~/utils/enum/common';
import { teacherRoute } from '~/utils/routes/teacher';
import { studentRoute } from '~/utils/routes/student';

type Props = {
  content?: string;
};

const cx = classNames.bind(styles);

const Material = (props: Props) => {
  //#region Destructuring Props
  const {} = props;
  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['common/home', 'common/base', 'teacher/material']);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  //#endregion Declare Hook

  //#region Selector
  const { modules: modulesResponse, isLoading } = useAppSelector((state) => state.moduleState);
  //#endregion Selector

  //#region Declare State
  const [modules, setModules] = useState<IModuleDetail[]>([]);
  const [page, setPage] = useState<number>(DEFAULT_PAGE);
  const [isLoadingOverlay, setIsLoadingOverlay] = useState<boolean>(false);
  //#endregion Declare State

  //#region Declare Memo
  //#endregion Declare Memo

  //#region Implement Hook
  useEffect(() => {
    handleGetModules();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setModules(modulesResponse?.responses ?? []);
  }, [modulesResponse]);
  //#endregion Implement Hook

  //#region Handle Function
  const handleBack = () => {
    navigate(-1);
  };

  const handleGetModules = async (query?: IQueryGetModule) => {
    await dispatch(
      getModules(
        query ?? {
          limit: DEFAULT_LIMIT_PAGE,
          page: page,
        }
      )
    )
      .unwrap()
      .finally(() => {
        setIsLoadingOverlay(false);
      });
  };

  const handleChangePage = (newPage: number) => {
    setPage(newPage);
    setIsLoadingOverlay(true);
    handleGetModules({
      limit: DEFAULT_LIMIT_PAGE,
      page: newPage,
    });
  };

  const renderContent = () => {
    if (isLoading && (!modulesResponse || modulesResponse.responses.length === 0)) {
      return (
        <div className={cx('spinnerContainer')}>
          <Spinner size='small' />
        </div>
      );
    }

    if (!modulesResponse || modulesResponse.responses.length === 0) {
      return (
        <div className={cx('spinnerContainer')}>
          <Text variant='contentSmall'>{t('common_no_data_available', { ns: 'common/base' })}</Text>
        </div>
      );
    }

    return (
      <View as='div' width={'100%'} height={'100%'}>
        <View as='div' className={cx('courseSelect')}>
          <BaseSelect
            value={modulesResponse.course?.id}
            options={[
              {
                label: modulesResponse.course?.name || '',
                value: modulesResponse.course?.id || '',
              },
            ]}
            disabled={true}
          />
        </View>

        <View as='div' className={cx('listContainer', 'desktopOnly')}>
          <Table layout='auto'>
            <Table.Head>
              <Table.Row>
                <Table.ColHeader id='Id' width='10%'>
                  {t('material_module_id_label', { ns: 'teacher/material' })}
                </Table.ColHeader>
                <Table.ColHeader id='Name' width='25%'>
                  {t('material_module_name_label', { ns: 'teacher/material' })}
                </Table.ColHeader>
                <Table.ColHeader id='Materials' width='65%'>
                  {t('material_module_materials_label', { ns: 'teacher/material' })}
                </Table.ColHeader>
              </Table.Row>
            </Table.Head>
            <Table.Body>
              {modules.map((data, index) => (
                <Table.Row key={index}>
                  <Table.Cell>
                    <span>{data.id}</span>
                  </Table.Cell>
                  <Table.Cell>
                    <span>{data.moduleName}</span>
                  </Table.Cell>
                  <Table.Cell>
                    <span>
                      <div className={cx('materialContainer')}>
                        {data.materials.map((material, index) => {
                          const isQuiz = material.type === MaterialTypeEnum.QUIZ;
                          const materialLink = isQuiz
                            ? `${teacherRoute.base}${teacherRoute.course}/${modulesResponse.course?.id}${teacherRoute.quiz}/${material.id}`
                            : material.link;

                          return (
                            <a href={materialLink} className={cx('itemLink')} target='_blank' rel='noopener noreferrer'>
                              {material.type === MaterialTypeEnum.QUIZ ? <IconQuizLine /> : <IconLinkLine />}
                              <Text variant='content'>{material.title}</Text>
                            </a>
                          );
                        })}
                      </div>
                    </span>
                  </Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table>
        </View>

        <View className={cx('paginationDesktop')}>
          <BasePagination currentPage={page} hasMore={modulesResponse?.hasMore} onChange={handleChangePage} />
        </View>

        <View as='div' className={cx('mobileOnly')}>
          {modules.map((data, index) => (
            <div key={index} className={cx('moduleCard')}>
              <div className={cx('cardRow')}>
                <strong>{t('material_module_id_label', { ns: 'teacher/material' })}:</strong>
                <span>{data.id}</span>
              </div>
              <div className={cx('cardRow')}>
                <strong>{t('material_module_name_label', { ns: 'teacher/material' })}:</strong>
                <span>{data.moduleName}</span>
              </div>
              <div className={cx('cardRow', 'materialRow')}>
                <strong>{t('material_module_materials_label', { ns: 'teacher/material' })}:</strong>
                <div className={cx('materialContainer')}>
                  {data.materials.map((material, index) => {
                    const isQuiz = material.type === MaterialTypeEnum.QUIZ;
                    const materialLink = isQuiz
                      ? `${teacherRoute.base}${teacherRoute.course}/${modulesResponse.course?.id}${teacherRoute.quiz}/${material.id}`
                      : material.link;

                    return (
                      <a href={materialLink} className={cx('itemLink')} target='_blank' rel='noopener noreferrer'>
                        {material.type === MaterialTypeEnum.QUIZ ? <IconQuizLine /> : <IconLinkLine />}
                        <Text variant='content'>{material.title}</Text>
                      </a>
                    );
                  })}
                </div>
              </div>
            </div>
          ))}
          <BasePagination currentPage={page} hasMore={modulesResponse?.hasMore} onChange={handleChangePage} />
        </View>
      </View>
    );
  };
  //#endregion Handle Function

  return (
    <div className={cx('viewPort')}>
      <div className={cx('backContainer')} onClick={handleBack}>
        <img src={icons.commonIconBack} alt={t('common_alt_img', { ns: 'common/base' })} className={cx('iconBack')} />
        <Text variant='content'>{t('common_back_button', { ns: 'common/base' })}</Text>
      </div>

      <div className={cx('container')}>
        <View as='div' className={cx('titleContainer')}>
          <div className={cx('headerTitle')}>
            <Heading level='h3'>{t('material_title', { ns: 'teacher/material' })}</Heading>
            <img
              className={cx('iconHeader')}
              src={icons.commonIconTuitionArea}
              alt={t('common_alt_img', { ns: 'common/base' })}
            />
          </div>
        </View>

        {renderContent()}
      </div>

      <Loading loading={isLoadingOverlay} overlay={true} />
    </div>
  );
};

export default Material;
